// src/components/home/FriendshipVibeToolsPreview.jsx
import React from 'react';
import { motion } from 'framer-motion';
import {
  Brain,
  Shield,
  MessageSquare,
  Heart,
  Users,
  Compass,
  Battery,
  Lightbulb,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import Button from '../shared/Button';
import { Card, CardContent } from '../shared/CardComponents';

const FriendshipVibeToolsPreview = ({ setCurrentView }) => {
  // Handle tool click - navigate to tools page with specific tool selected
  const handleToolClick = (toolId) => {
    // Store selected tool in localStorage so VibeToolsPage can open it
    localStorage.setItem('selectedTool', toolId);
    setCurrentView('tools');
  };

  const tools = [
    {
      id: 'social-pain-laboratory',
      name: 'Social Pain Laboratory',
      emoji: '🧪',
      icon: Brain,
      color: 'purple',
      gradient: 'from-purple-500/20 to-pink-500/20',
      border: 'border-purple-500/30',
      description: 'Understand why rejection hurts and how your brain processes social pain',
      benefit: 'Turn social setbacks into scientific insights',
      tag: 'Science-Backed'
    },
    {
      id: 'friendship-grief-companion',
      name: 'Friendship Grief Companion',
      emoji: '💜',
      icon: Heart,
      color: 'pink',
      gradient: 'from-pink-500/20 to-rose-500/20',
      border: 'border-pink-500/30',
      description: 'Navigate the unique pain of losing a friendship with compassion',
      benefit: 'Heal and honour what you\'ve lost',
      tag: 'Emotional Support'
    },
    {
      id: 'boundary-building-blueprint',
      name: 'Boundary Building Blueprint',
      emoji: '🛡️',
      icon: Shield,
      color: 'blue',
      gradient: 'from-blue-500/20 to-cyan-500/20',
      border: 'border-blue-500/30',
      description: 'Design and communicate boundaries that protect your wellbeing',
      benefit: 'Say no without guilt, yes with confidence',
      tag: 'Practical Skills'
    },
    {
      id: 'conflict-resolution-simulator',
      name: 'Conflict Resolution Simulator',
      emoji: '⚖️',
      icon: MessageSquare,
      color: 'orange',
      gradient: 'from-orange-500/20 to-yellow-500/20',
      border: 'border-orange-500/30',
      description: 'Practise difficult conversations in a safe, guided environment',
      benefit: 'Build confidence before real-world conflicts',
      tag: 'Interactive Practice'
    },
    {
      id: 'group-dynamics-decoder',
      name: 'Group Dynamics Decoder',
      emoji: '👥',
      icon: Users,
      color: 'green',
      gradient: 'from-green-500/20 to-emerald-500/20',
      border: 'border-green-500/30',
      description: 'Decode complex group interactions and find your place',
      benefit: 'Navigate group friendships with clarity',
      tag: 'Social Intelligence'
    },
    {
      id: 'digital-drama-shield',
      name: 'Digital Drama Shield',
      emoji: '📱',
      icon: Shield,
      color: 'teal',
      gradient: 'from-teal-500/20 to-cyan-500/20',
      border: 'border-teal-500/30',
      description: 'Manage online conflicts and protect your digital wellbeing',
      benefit: 'Stay connected, stay protected',
      tag: 'Digital Skills'
    },
    {
      id: 'social-battery-optimizer',
      name: 'Social Battery Optimiser',
      emoji: '🔋',
      icon: Battery,
      color: 'yellow',
      gradient: 'from-yellow-500/20 to-orange-500/20',
      border: 'border-yellow-500/30',
      description: 'Track and optimise your social energy for sustainable friendships',
      benefit: 'Recharge smarter, connect longer',
      tag: 'Self-Care'
    },
    {
      id: 'people-finder-compass',
      name: 'People Finder Compass',
      emoji: '🧭',
      icon: Compass,
      color: 'indigo',
      gradient: 'from-indigo-500/20 to-purple-500/20',
      border: 'border-indigo-500/30',
      description: 'Discover where and how to find friendships that fit',
      benefit: 'Meet your people, your way',
      tag: 'Connection Building'
    }
  ];

  const tagColors = {
    'Science-Backed': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    'Emotional Support': 'bg-pink-500/20 text-pink-300 border-pink-500/30',
    'Practical Skills': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    'Interactive Practice': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    'Social Intelligence': 'bg-green-500/20 text-green-300 border-green-500/30',
    'Digital Skills': 'bg-teal-500/20 text-teal-300 border-teal-500/30',
    'Self-Care': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    'Connection Building': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30'
  };

  return (
    <section className="py-16 px-6 bg-gradient-to-b from-slate-900 to-slate-800">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mx-auto mb-8 max-w-md"
          >
            <img
              src="/images/friendship-toolkit.png"
              alt="Friendship Toolkit"
              className="w-full h-auto rounded-2xl shadow-2xl shadow-teal-500/20"
            />
          </motion.div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500/20 to-purple-500/20 rounded-full border border-teal-500/30 mb-6">
            <Sparkles className="w-4 h-4 text-teal-400" />
            <span className="text-sm font-medium text-teal-300">Your Interactive Friendship Toolkit</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Tools That Work{' '}
            <span className="bg-gradient-to-r from-teal-400 to-purple-400 bg-clip-text text-transparent">
              With You
            </span>
          </h2>

          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Beyond theory—interactive tools to experiment, practice, and build real skills.
            Your personal toolkit for navigating every friendship challenge.
          </p>
        </motion.div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {tools.map((tool, index) => {
            const Icon = tool.icon;
            return (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <Card
                  className={`bg-gradient-to-br ${tool.gradient} border ${tool.border} hover:scale-105 transition-all duration-300 cursor-pointer group h-full`}
                  onClick={() => handleToolClick(tool.id)}
                >
                  <CardContent className="p-6 flex flex-col h-full">
                    {/* Tool Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="text-4xl mb-2">{tool.emoji}</div>
                      <div className={`px-2 py-1 rounded-md border text-xs font-medium ${tagColors[tool.tag]}`}>
                        {tool.tag}
                      </div>
                    </div>

                    {/* Tool Name */}
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-teal-300 transition-colors">
                      {tool.name}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-slate-300 mb-4 flex-grow">
                      {tool.description}
                    </p>

                    {/* Benefit Highlight */}
                    <div className="flex items-start gap-2 p-3 bg-white/5 rounded-lg border-l-2 border-teal-400">
                      <Lightbulb className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-teal-300 font-medium">
                        {tool.benefit}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Why This Matters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-8 mb-8"
        >
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl mb-3">🎯</div>
              <h4 className="text-lg font-bold text-white mb-2">Practise Before Pressure</h4>
              <p className="text-sm text-slate-300">
                Try new skills in a safe space before real-world situations
              </p>
            </div>
            <div>
              <div className="text-4xl mb-3">🧠</div>
              <h4 className="text-lg font-bold text-white mb-2">Science Meets Support</h4>
              <p className="text-sm text-slate-300">
                Evidence-based tools wrapped in empathy and understanding
              </p>
            </div>
            <div>
              <div className="text-4xl mb-3">💪</div>
              <h4 className="text-lg font-bold text-white mb-2">Build Real Confidence</h4>
              <p className="text-sm text-slate-300">
                See your growth, track your progress, feel the difference
              </p>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center"
        >
          <Button
            onClick={() => setCurrentView('tools')}
            size="lg"
            className="bg-gradient-to-r from-teal-500 to-purple-500 hover:from-teal-400 hover:to-purple-400 text-white font-bold shadow-lg shadow-teal-500/30"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            Explore Your Toolkit
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-sm text-slate-400 mt-3">
            8 interactive tools • Science-backed • Always available
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FriendshipVibeToolsPreview;
